import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import {
    floorGameColor as color,
    floorGameDuration as duration,
    floorGameEasing as easing,
    iconSize,
    styledInteraction,
    styledLayout,
    styledMedia,
    styledReset,
    styledTypography,
} from '@pkg/primitives';
import { percentToHex } from '@pkg/utils';

import { tokens } from '../../tokens';

import { DeathGameSharedVariant, deathGameVariants } from './DeathGame.motion';

interface StyledDeathGameProps {
    $compact?: boolean;
}

// TODO: Temporary until we add the WebGL flames.
const redAlpha6 = `${color.red}${percentToHex(6)}`;

const labelBase = css`
    font-size: var(--yat_death-game-label-font-size);
    font-weight: 700;
    letter-spacing: -0.01em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.6);
`;

// TODO: We only specify a `min-height` for `PriceGame` and `DeathGame`.
// We can still end up with a layout shift when toggling between states.
// We should investigate a clever solution for this.
export const StyledDeathGame = styled(motion.div).attrs({
    variants: deathGameVariants,
    initial: DeathGameSharedVariant.Initial,
    animate: DeathGameSharedVariant.Show,
    exit: DeathGameSharedVariant.Exit,
})<StyledDeathGameProps>`
    --yat_death-game-min-height: ${({ $compact }) =>
        tokens.card.children.minHeight[$compact ? 'compact' : 'default']}px;
    --yat_death-game-interior-gap: ${({ $compact }) =>
        tokens.deathGame.interior.gap[$compact ? 'compact' : 'default']}px;
    --yat_death-game-label-font-size: ${({ $compact }) =>
        tokens.deathGame.label.fontSize[$compact ? 'compact' : 'default']}px;

    display: grid;
    align-items: center;
    grid-area: 1 / 1;
    // Required in order to prevent layout shifting.
    min-height: var(--yat_death-game-min-height);

    &::before {
        ${styledInteraction.interactionDisable};
        ${styledLayout.positionCover()};
        content: '';
        display: block;
        z-index: ${tokens.priceGame.zindex.glow};
        border-radius: ${tokens.card.wrapper.radius}px;
        background: linear-gradient(to bottom, transparent, transparent, ${redAlpha6});
    }
`;

export const Information = styled.button.attrs({ type: 'button' })`
    ${styledReset.button};
    ${styledMedia.iconWrapper};
    position: absolute;
    top: ${tokens.deathGame.information.offset}px;
    right: ${tokens.deathGame.information.offset}px;
    width: ${iconSize.small}px;
    height: ${iconSize.small}px;
    color: rgba(255, 255, 255, 0.4);
    transition: color ${duration.normal}ms ${easing.cubic};

    &:focus,
    &:hover {
        color: rgba(255, 255, 255, 0.6);
    }

    &:active {
        color: rgba(255, 255, 255, 0.4);
    }
`;

export const Interior = styled.div`
    z-index: ${tokens.deathGame.zindex.interior};
    position: relative;
    display: grid;
    place-content: center;
    place-items: center;
    gap: var(--yat_death-game-interior-gap);
    // Not applying horizontal padding in order to afford
    // a little bit of addition real estate for the Clock.
    padding-top: ${tokens.card.children.padding}px;
    padding-bottom: ${tokens.card.children.padding}px;
`;

export const Label = styled.p`
    ${styledTypography.allianceBase};
    ${labelBase};
`;

export const Clock = styled.ol`
    ${styledReset.list};
    display: flex;
    gap: ${tokens.deathGame.clock.gap.default}px;
`;

export const ClockItem = styled.li`
    display: grid;
    gap: ${tokens.deathGame.clock.item.gap}px;
    text-align: center;

    &:first-child {
        --clock-item-content-before: none;
    }
`;

export const ClockCount = styled.div`
    position: relative;

    // Divider
    &::before {
        content: var(--clock-item-content-before, '');
        position: absolute;
        top: 0;
        bottom: 0;
        left: -${tokens.deathGame.clock.gap.default / 2}px;
        display: block;
        margin-top: auto;
        margin-bottom: auto;
        width: ${tokens.deathGame.clock.divider.width}px;
        height: ${tokens.deathGame.clock.divider.height}px;
        background-color: rgba(255, 255, 255, 0.2);
    }
`;

export const ClockLabel = styled.p`
    ${styledTypography.allianceBase};
    ${labelBase};
`;
